const content = {
  company: {
    name: "Alpha Trust",
  },

  hero: {
    headline: "Integrated Energy\nInfinite Potential",
    subheadline: "A powerhouse of renewable energy, dedicated to evolving Romania's energy landscape.",
    background_image: "/alpha-trust-hero.jpg",
  },

  about: {
    overline: "ABOUT",
    heading: "We balance environmental value with technical excellence — empowering communities, industries and systems to thrive in the renewable era.",
    paragraph1: "Alpha Trust is a forward-thinking company dedicated to building resilient, sustainable power systems for an evolving world. Backed by over 30 years of industry experience, including more than 15 years in the local market, we bring technical knowledge and a long-term perspective to every project we undertake. With a portfolio that spans from grid balancing to specialized technical studies, we design and deliver integrated renewable energy solutions that address both today's challenges and tomorrow's opportunities.",
    paragraph2: "Rooted in innovation and driven by impact, Alpha Trust combines engineering expertise with environmental responsibility. Our projects are designed to enhance the stability, flexibility, and sustainability of the entire energy ecosystem.",
    image_url: "/about_image.avif",
    stat1_value: "30+",
    stat1_label: "YEARS IN THE ENERGY SECTOR",
    stat2_value: "15+",
    stat2_label: "YEARS IN THE ROMANIAN MARKET",
    stat3_value: "",
    stat3_label: "",
  },

  stats: {
    heading: "Building Energy.\nReducing Emissions.",
    intro: "The scale of what we build, the energy we produce, and the emissions we avoid — these numbers are how our progress takes shape.",
    items: [
      {
        id: "stat-1",
        number: "01",
        label: "Installed Capacity",
        value: "137",
        unit: "MW",
        description: "We operate a total of 137 MW installed capacity, currently active across multiple technologies and sites.",
        image_url: "/first_card_image.png",
      },
      {
        id: "stat-2",
        number: "02",
        label: "Annual Production",
        value: "122,000",
        unit: "MWh",
        description: "We generate approximately 122,000 MWh of electricity annually, from all technologies across our operational sites.",
        image_url: "/second_card_image.jpg",
      },
      {
        id: "stat-3",
        number: "03",
        label: "CO\u2082 Avoided",
        value: "68,300",
        unit: "Tonnes",
        description: "Our energy production prevents 68,300 tonnes of CO\u2082 emissions annually, significantly reducing the carbon footprint of the regions we serve.",
        image_url: "/third_card_image.jpg",
      },
    ],
  },

  solutions: {
    overline: "OUR SOLUTIONS",
    heading: "Smart, Scalable, Sustainable.",
    intro: "Alpha Trust brings together a diversified portfolio of energy solutions.",
    items: [
      {
        id: "sol-1",
        number: "01",
        icon: "Zap",
        title: "Grid Balancing Hub",
        paragraph1: "Designed to stabilize real-time fluctuations in supply and demand, it combines fast-response assets like battery storage with dispatchable generation to ensure flexibility where the grid needs it most. The grid balancing hub is made up of a project with gas engines and battery energy storage systems (BESS), and a project with solar panels and BESS — together providing both firm capacity and renewable integration support.",
        paragraph2: "As more renewable sources enter the energy mix, the ability to balance volatility becomes critical — not only for grid reliability, but also for accelerating the shift away from carbon-intensive backup power. The grid balancing hub enables that shift by responding instantly, efficiently, and cleanly, making it an essential part of tomorrow's energy infrastructure.",
      },
      {
        id: "sol-2",
        number: "02",
        icon: "Sun",
        title: "Solar Energy",
        paragraph1: "Our solar projects provide clean, efficient power for industrial and utility use. Built for long-term reliability, they can operate alone or with storage, helping cut emissions and support sustainable energy use.",
        paragraph2: "Across all sites, the plants use robust solar panels with proven performance in local irradiation conditions, ensuring steady and predictable production year-round.",
      },
      {
        id: "sol-3",
        number: "03",
        icon: "Leaf",
        title: "Biogas",
        paragraph1: "Our biogas facility converts organic waste into renewable energy and fertilizer through anaerobic digestion. It reduces emissions, supports waste reuse, and promotes a circular economy.",
        paragraph2: "The plant integrates advanced technologies for efficient biogas production and energy generation, ensuring high operational performance and environmental responsibility.",
      },
      {
        id: "sol-4",
        number: "04",
        icon: "Compass",
        title: "Development & Operations",
        paragraph1: "From early-stage feasibility to full operational management, our development and operations team oversees the entire project lifecycle with precision and expertise.",
        paragraph2: "We bring over 30 years of industry experience to every project, ensuring technical excellence, regulatory compliance, and long-term performance across our entire portfolio.",
      },
    ],
  },

  projects: {
    overline: "PROJECTS",
    heading: "Powering Progress Across Technologies",
    intro: "Our portfolio spans a range of energy solutions — from grid balancing and battery storage to solar power and biogas production. Each project plays a role in building a cleaner, more resilient energy system.",
    items: [
      {
        id: "proj-1",
        title: "Grid Balancing Hub",
        description: "Our grid hub balances energy supply and demand with fast-response battery storage and flexible generation. It stabilizes the grid, supports renewables, and scales efficiently for a cleaner, more resilient network.",
        hero_image: "/new-render-racari.jpeg",
        stat_groups: [
          {
            title: "Gas Engines & BESS",
            stats: [
              { label: "TOTAL INSTALLED CAPACITY", value: "52 MW" },
              { label: "TOTAL ANNUAL PRODUCTION", value: "50,000 MWh" },
              { label: "CO\u2082 AVOIDED", value: "22,500 tonnes / year" },
            ],
          },
          {
            title: "Solar & BESS",
            stats: [
              { label: "TOTAL INSTALLED CAPACITY", value: "70 MW" },
              { label: "TOTAL ANNUAL PRODUCTION", value: "45,000 MWh" },
              { label: "CO\u2082 AVOIDED", value: "32,000 tonnes / year" },
            ],
          },
        ],
        tech_heading: "",
        tech_intro: "The Grid Balancing Hub integrates multiple technologies to provide stability and flexibility in modern energy systems. Each component is designed to respond quickly to fluctuations, support renewable integration, and maximize overall system efficiency.",
        technologies: [
          {
            id: "tech-1-1",
            title: "Hybrid Power Generation",
            description: "Two gas engines of 20 MW each, operating alongside a 6 MWh battery energy storage system (BESS), ensure fast ramp-up and reliable grid balancing.",
          },
          {
            id: "tech-1-2",
            title: "Battery Storage",
            description: "A new 40 MW / 160 MWh battery storage system and a 30 MWp solar plant, currently under construction, will enhance flexibility and enable renewable charging of the batteries.",
          },
          {
            id: "tech-1-3",
            title: "Data Center",
            description: "Future developments include a 30 MW Data Center, leveraging on-site energy resources for efficient and sustainable operations.",
          },
        ],
      },
      {
        id: "proj-2",
        title: "Solar Energy",
        description: "Our solar projects provide clean, efficient power for industrial and utility use. Built for long-term reliability, they can operate alone or with storage, helping cut emissions and support sustainable energy use.",
        hero_image: "/solar_project_image.png",
        stat_groups: [],
        stats: [
          { label: "TOTAL INSTALLED CAPACITY", value: "32 MW" },
          { label: "TOTAL ANNUAL PRODUCTION", value: "50,000 MWh" },
          { label: "CO\u2082 AVOIDED", value: "22,100 tonnes / year" },
        ],
        tech_heading: "",
        tech_intro: "The solar projects are equipped with reliable photovoltaic technology and high-quality components to ensure consistent energy generation and long-term operational stability.",
        technologies: [
          {
            id: "tech-2-1",
            title: "High-Efficiency PV Modules",
            description: "Across all sites, the plants use robust solar panels with proven performance in local irradiation conditions, ensuring steady production.",
          },
          {
            id: "tech-2-2",
            title: "Optimized System Design",
            description: "Each installation is engineered to achieve high specific energy output, with layouts and equipment selected to maximize efficiency and production under site-specific conditions.",
          },
          {
            id: "tech-2-3",
            title: "Planned Capacity Expansion",
            description: "An additional 36 MW is planned across sites, leveraging available land and infrastructure for efficient scaling of the portfolio.",
          },
        ],
      },
      {
        id: "proj-3",
        title: "Biogas Plant",
        description: "Our biogas facility converts organic waste into renewable energy and fertilizer through anaerobic digestion. It reduces emissions, supports waste reuse, and promotes a circular economy.",
        hero_image: "/biogas_project_image.jpg",
        stat_groups: [],
        stats: [
          { label: "TOTAL INSTALLED CAPACITY", value: "3 MW" },
          { label: "TOTAL ANNUAL PRODUCTION", value: "12,000 MWh" },
          { label: "CO\u2082 AVOIDED", value: "1,800 tonnes / year" },
        ],
        tech_heading: "",
        tech_intro: "The plant integrates advanced technologies for efficient biogas production and energy generation, ensuring high operational performance and environmental responsibility.",
        technologies: [
          {
            id: "tech-3-1",
            title: "Anaerobic Digestion",
            description: "Organic waste is processed in sealed digesters where microorganisms break down the material, producing biogas with high methane content.",
          },
          {
            id: "tech-3-2",
            title: "Combined Heat and Power (CHP) Units",
            description: "The generated biogas fuels CHP engines, delivering both electricity and thermal energy for optimized resource utilization.",
          },
          {
            id: "tech-3-3",
            title: "Biogas Purification & Digestate Valorization",
            description: "The system includes gas conditioning for optimal engine performance and produces digestate, a nutrient-rich byproduct used as organic fertilizer.",
          },
        ],
      },
    ],
  },

  subsidiaries: {
    overline: "PART OF THE GROUP",
    items: [
      { id: "sub-1", name: "Hydroplan", logo_url: "/subsidiary-hydroplan.png", link: null },
      { id: "sub-2", name: "Montana Energy", logo_url: "/subsidiary-montana.png", link: "/announcements" },
      { id: "sub-3", name: "Ecoterra", logo_url: "/subsidiary-ecoterra.png", link: "https://ecoterrabiogas.ro/", external: true },
      { id: "sub-4", name: "Navios", logo_url: "/subsidiary-navios.png", link: "https://www.navios.ro/", external: true },
    ],
  },

  contact: {
    heading: "Get better energy, now.",
    email: "office@alphatrust.com.ro",
    country: "ROMANIA",
    address: "Calea Bucurestilor 3A\n075100 Otopeni\nIlfov",
    phone: "+40 374 305 791",
  },

  footer: {
    copyright: "Alpha Trust \u00a9 2026. All rights reserved.",
    legal: "This website and its content are the property of Alpha Trust and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of any material is strictly prohibited.",
    nav_links: [
      { label: "About us", anchor: "#about", number: "01" },
      { label: "Projects", anchor: "#projects", number: "02" },
    ],
  },
};

export default content;
